function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useEffect, useMemo, useState } from 'react';
import { CustomModal, Button, TableList, Page, Spinner, Typography, PageEmptyState, PageErrorState, PageContentHeader, useStateInRef, FilterPickerGroup, Searchbox, MultiSelectFilterPicker } from 'seagull';
import keyBy from 'lodash/keyBy';
import orderBy from 'lodash/orderBy';
import sortBy from 'lodash/sortBy';
import { useAppConfig, useStore, useStoreActions } from "../../../store/store-context";
import { modalSize } from "../../../infra/modals";
import { CreateProposalButton } from "../../../components/CreateProposalButton/CreateProposalButton";
import { handlePromise } from "../../../infra/utils";
import { useServiceTemplateApi } from "../../../hooks/useServiceTemplateApi";
import { QboItemsStatus } from "../../../types/api/qbo.model";
import { selectors } from "../../../store/state-selectors";
import { useOnMount } from "../../../hooks/useOnMount";
import NoDataIcon from "../../assets/NoDataIcon";
import styles from "./MapServicesModal.module.scss";
import { columnDefs, getSearchedServicesAndFilteredOptions, headerItems } from "./MapServicesModal.utils";
import MapServicesRow from "./MapServicesRow";
import { serviceTemplateModel } from "../../../models";
export function MapServicesModal(props) {
  const {
    dismissModal,
    onChange,
    serviceTemplateIds
  } = props;
  const {
    data: qboItems,
    loading: loadingQboItems,
    error: errorQboItems
  } = useStore(state => state.qboItems);
  const businessId = useStore(selectors.user.businessId);
  const {
    api,
    logBI,
    fetchQboItems
  } = useStoreActions();
  const {
    inputDebounceTime
  } = useAppConfig();
  const [modalState, setModalState] = useState('loading');
  const [serviceTemplates, setServiceTemplates] = useState();
  const [loadingServiceTemplates, setLoadingServiceTemplates] = useState(true);
  const [errorServiceTemplates, setErrorServiceTemplates] = useState(false);
  const [search, setSearch] = useState('');
  const [serviceMappingStatusFilter, setServiceMappingStatusFilter] = useState([]);
  const serviceTemplateApi = useServiceTemplateApi();
  const isLoading = loadingQboItems || loadingServiceTemplates;
  const vendorId = useStore(selectors.user.businessId);
  const userId = useStore(selectors.user.id);
  const getStateInRef = useStateInRef({
    serviceTemplates,
    errorQboItems,
    errorServiceTemplates,
    qboItems,
    userId,
    vendorId
  });
  const {
    filteredServices,
    serviceFilterOptions
  } = useMemo(() => getSearchedServicesAndFilteredOptions({
    services: serviceTemplates || [],
    search,
    statusFilters: serviceMappingStatusFilter
  }), [serviceTemplates, search, serviceMappingStatusFilter]);
  useOnMount(() => {
    refreshData();
  });
  useEffect(() => {
    const {
      serviceTemplates,
      errorQboItems,
      errorServiceTemplates,
      qboItems,
      userId,
      vendorId
    } = getStateInRef();
    const hasError = errorQboItems || (qboItems === null || qboItems === void 0 ? void 0 : qboItems.status) === QboItemsStatus.RequestTimeout || errorServiceTemplates;
    const noAnchorServiceTemplates = serviceTemplates && serviceTemplates.length === 0;
    const hasData = serviceTemplates && qboItems;
    if (isLoading) setModalState('loading');else if (hasError) setModalState('error');else if (noAnchorServiceTemplates) setModalState('empty');else if (hasData) setModalState('valid');
    if (hasError) {
      logBI(12006, {
        vendorId,
        userId
      });
    }
  }, [isLoading]);
  async function fetchServiceTemplates() {
    setLoadingServiceTemplates(true);
    try {
      const services = await serviceTemplateApi.get({
        includeDeleted: true,
        includeAdhocCharges: true
      });
      const filteredServices = serviceTemplateIds !== null && serviceTemplateIds !== void 0 && serviceTemplateIds.length ? services.raw.filter(s => serviceTemplateIds.includes(s.id)) : services.raw;
      const orderedByName = orderBy(filteredServices, ['name'], ['asc']);
      const sortedByIntegrationStatus = sortBy(orderedByName, s => !!s.integratedId);
      const sortedByDeletedStatus = sortBy(sortedByIntegrationStatus, serviceTemplateModel.isDeleted);
      setServiceTemplates(sortedByDeletedStatus);
    } catch (err) {
      setErrorServiceTemplates(true);
    } finally {
      setLoadingServiceTemplates(false);
    }
  }
  const numOfMappedServices = useMemo(() => {
    const mappedServices = serviceTemplates === null || serviceTemplates === void 0 ? void 0 : serviceTemplates.filter(c => !!c.integratedId);
    return (mappedServices === null || mappedServices === void 0 ? void 0 : mappedServices.length) || 0;
  }, [serviceTemplates]);
  const qboItemsDictionary = useMemo(() => {
    return keyBy(qboItems === null || qboItems === void 0 ? void 0 : qboItems.items, 'id');
  }, [qboItems]);
  async function refreshData() {
    fetchServiceTemplates();
    fetchQboItems();
  }
  async function onChangeMap(serviceTemplate, qboItemId) {
    const linkResponse = await handlePromise(api.qbo.linkServiceTemplate(businessId, serviceTemplate.id, qboItemId));
    if (linkResponse.success) {
      onChange && onChange();
      const updatedServiceTemplate = await handlePromise(api.serviceTemplate.getById(businessId, serviceTemplate.id));
      if (updatedServiceTemplate.success) {
        const newAnchorServiceTemplates = serviceTemplates === null || serviceTemplates === void 0 ? void 0 : serviceTemplates.map(s => {
          const isDesiredServiceTemplate = s.id == serviceTemplate.id;
          return isDesiredServiceTemplate ? updatedServiceTemplate.data : s;
        });
        setServiceTemplates(newAnchorServiceTemplates);
      } else {
        throw updatedServiceTemplate.error;
      }
    } else {
      throw linkResponse.error;
    }
  }
  return /*#__PURE__*/React.createElement(CustomModal, _extends({
    "data-testid": "qbo-map-services-modal",
    footer: modalState === 'valid' && /*#__PURE__*/React.createElement("div", {
      className: styles.footer
    }, serviceTemplates && /*#__PURE__*/React.createElement("div", {
      "data-testid": "total-map-status"
    }, /*#__PURE__*/React.createElement(Typography, null, "".concat(numOfMappedServices, " of ").concat(serviceTemplates.length, " services mapped"))), /*#__PURE__*/React.createElement(Button, {
      onClick: dismissModal,
      "data-testid": "qbo-map-services-close"
    }, "Close"))
  }, modalSize.large, {
    onClose: dismissModal
  }), /*#__PURE__*/React.createElement(Page, {
    height: "100%",
    header: "Map Services to QuickBooks Online",
    subheader: /*#__PURE__*/React.createElement(React.Fragment, null, "Map Anchor services to QuickBooks Online items."),
    skin: "secondary",
    "data-testid": "qbo-map-services"
  }, /*#__PURE__*/React.createElement(TableList.Provider, {
    columnDefs: columnDefs
  }, modalState === 'loading' && /*#__PURE__*/React.createElement(LoadingState, null), modalState === 'empty' && /*#__PURE__*/React.createElement(EmptyState, null), modalState === 'error' && /*#__PURE__*/React.createElement(ErrorState, {
    onRefresh: refreshData
  }), modalState === 'valid' && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(PageContentHeader, null, /*#__PURE__*/React.createElement("div", {
    className: styles.filterContainer
  }, /*#__PURE__*/React.createElement(FilterPickerGroup, {
    flexWrap: true,
    "data-testid": "map-services-modal-filters"
  }, /*#__PURE__*/React.createElement(Searchbox, {
    value: search,
    "data-testid": "map-services-modal-search",
    debounceTime: inputDebounceTime !== null && inputDebounceTime !== void 0 ? inputDebounceTime : 500,
    onChange: event => setSearch(event.target.value),
    onClear: () => setSearch('')
  }), /*#__PURE__*/React.createElement(MultiSelectFilterPicker, {
    "data-testid": "qbo-mapping-status-filter-picker",
    value: serviceMappingStatusFilter,
    options: serviceFilterOptions,
    label: "Is mapped",
    onChange: setServiceMappingStatusFilter
  }))), /*#__PURE__*/React.createElement(TableList.Header, {
    items: headerItems
  })), filteredServices === null || filteredServices === void 0 ? void 0 : filteredServices.map((serviceTemplate, index) => {
    const integratedId = serviceTemplate.integratedId;
    const integratedQboItem = integratedId ? qboItemsDictionary[integratedId] : undefined;
    return /*#__PURE__*/React.createElement(MapServicesRow, {
      serviceTemplate: serviceTemplate,
      qboItems: qboItems === null || qboItems === void 0 ? void 0 : qboItems.items,
      integratedQboItem: integratedQboItem,
      onChangeMap: onChangeMap,
      testid: "map-services-row-".concat(index),
      key: serviceTemplate.id
    });
  })))));
}
const LoadingState = () => /*#__PURE__*/React.createElement("div", {
  className: styles.loading
}, /*#__PURE__*/React.createElement(Spinner, {
  size: 40,
  "data-testid": "map-services-loading"
}));
const EmptyState = () => /*#__PURE__*/React.createElement(PageEmptyState, {
  "data-testid": "map-services-empty-state",
  illustration: /*#__PURE__*/React.createElement(NoDataIcon, null),
  message: "In order to map with your QuickBooks online account, start by creating an agreement",
  actions: /*#__PURE__*/React.createElement(CreateProposalButton, {
    text: "Create a new agreement",
    hidePrefix: true,
    "data-testid": "map-service-new-btn"
  })
});
const ErrorState = _ref => {
  let {
    onRefresh
  } = _ref;
  return /*#__PURE__*/React.createElement(PageErrorState, {
    "data-testid": "map-services-error-state",
    message: "Failed to retrieve the relevant data please try again later",
    onRefresh: onRefresh,
    skin: "secondary"
  });
};